import { triggerHaptic } from "@/lib/nativeHaptics";

export type BottomNavTab = "home" | "saved" | "wardrobe" | "profile";

interface BottomNavProps {
  activeTab: BottomNavTab;
  onTabChange: (tab: BottomNavTab) => void;
  onAddOutfitTap: () => void;
  savedBadgeCount?: number;
}

const TAB_INACTIVE = "rgba(245,240,235,0.5)";
const TAB_ACTIVE = "#F5F0EB";

interface TabSpec {
  id: BottomNavTab;
  icon: string;
  label: string;
}

const TABS_LEFT: TabSpec[] = [
  { id: "home", icon: "ti-home", label: "Home" },
  { id: "saved", icon: "ti-bookmark", label: "Saved" },
];

const TABS_RIGHT: TabSpec[] = [
  { id: "wardrobe", icon: "ti-hanger", label: "Wardrobe" },
  { id: "profile", icon: "ti-user", label: "Profile" },
];

function TabButton({
  spec,
  active,
  badgeCount,
  onClick,
}: {
  spec: TabSpec;
  active: boolean;
  badgeCount?: number;
  onClick: () => void;
}) {
  const color = active ? TAB_ACTIVE : TAB_INACTIVE;
  const ariaLabel =
    badgeCount && badgeCount > 0
      ? `${spec.label} tab, ${badgeCount} new ${badgeCount === 1 ? "item" : "items"}`
      : `${spec.label} tab`;
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      style={{
        flex: 1,
        height: 49,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        background: "transparent",
        border: "none",
        cursor: "pointer",
        position: "relative",
        color,
      }}
    >
      <span style={{ position: "relative", lineHeight: 0 }}>
        <i className={`ti ${spec.icon}`} style={{ fontSize: 24, color }} />
        {badgeCount && badgeCount > 0 ? (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: -2,
              right: -4,
              width: 8,
              height: 8,
              borderRadius: 999,
              background: "#C84B2F",
            }}
          />
        ) : null}
      </span>
      <span style={{ fontSize: 10, color, letterSpacing: 0.1 }}>
        {spec.label}
      </span>
    </button>
  );
}

export default function BottomNav({
  activeTab,
  onTabChange,
  onAddOutfitTap,
  savedBadgeCount = 0,
}: BottomNavProps) {
  const handleTab = (tab: BottomNavTab) => {
    if (tab === activeTab) {
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    void triggerHaptic("tab-switch");
    onTabChange(tab);
  };

  return (
    <nav
      role="navigation"
      aria-label="Primary"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 50,
        background: "#2C2820",
        height: "calc(49px + var(--safe-bottom))",
        paddingBottom: "var(--safe-bottom)",
        display: "flex",
        alignItems: "stretch",
      }}
    >
      {TABS_LEFT.map((spec) => (
        <TabButton
          key={spec.id}
          spec={spec}
          active={activeTab === spec.id}
          badgeCount={spec.id === "saved" ? savedBadgeCount : undefined}
          onClick={() => handleTab(spec.id)}
        />
      ))}
      <div
        style={{
          flex: 1,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <button
          type="button"
          aria-label="Add outfit"
          onClick={() => {
            void triggerHaptic("primary-action");
            onAddOutfitTap();
          }}
          className="active:scale-95 transition-transform duration-100"
          style={{
            width: 48,
            height: 48,
            borderRadius: 999,
            background: "#C84B2F",
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transform: "translateY(-12px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            cursor: "pointer",
          }}
        >
          <i
            className="ti ti-plus"
            style={{ fontSize: 24, color: "#FFFFFF" }}
          />
        </button>
      </div>
      {TABS_RIGHT.map((spec) => (
        <TabButton
          key={spec.id}
          spec={spec}
          active={activeTab === spec.id}
          onClick={() => handleTab(spec.id)}
        />
      ))}
    </nav>
  );
}
